<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVisitasStore } from 'dashboard/stores/visitas';
import { useMapGetter } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import AgentAPI from 'dashboard/api/agents';
import ImovelAPI from 'dashboard/api/imoveis';

import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisContactSearch from 'dashboard/components-next/LaisContactSearch.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisPropertySearch from 'dashboard/components-next/LaisPropertySearch.vue';
import {
  ENTRY_CONDITIONS_LABELS,
  ENTRY_DOORMAN_OPTIONS,
  ENTRY_AUTHORIZATION_OPTIONS,
  ENTRY_KEY_TYPE_OPTIONS,
  ENTRY_OCCUPANCY_OPTIONS,
  VISIT_HIGH_DEMAND_LABEL,
  VISIT_INTENTION_OPTIONS,
  doormanLabel,
  entryAccessHelper,
  entryAccessLabel,
  keyTypeLabel,
  normalizeEntryAccessKey,
  occupancyLabel,
} from '../utils/visitFlowCopy';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const currentAccount = useMapGetter('getCurrentAccount');
const store = useVisitasStore();
const isEditing = !!route.params.id;
const isLoading = ref(false);
const isSaving = ref(false);
const propertyMaterializing = ref(false);
const availability = ref(null);
const availabilityLoading = ref(false);
const availabilityError = ref('');
const availabilityRequestId = ref(0);
const originalScheduledAt = ref('');
const selectedAvailabilityDay = ref(0);
const showVisitConfirmationDialog = ref(false);
const confirmationDialogAcknowledged = ref(false);
const showClosingDialog = ref(false);
const pendingLeaveRoute = ref(null);
const allowLeaving = ref(false);
const initialSnapshot = ref('');
const editingAccess = ref(false);
const KEY_HOLDER_4035_MESSAGE =
  '4035: informe o responsável pelas chaves para confirmar esta visita.';
const REQUEST_FEEDBACK_STORAGE_PREFIX = 'lais:visit-request-feedback';
const REGISTER_FEEDBACK_REQUIRED_MESSAGE =
  'O que o visitante achou do imóvel? é obrigatório.';
const REGISTER_FEEDBACK_COPY = Object.freeze({
  title: 'Mensagem ao proprietário',
  helper: 'Nos ajude a informá-lo como o imóvel está sendo visto.',
  question: 'O que o visitante achou do imóvel?',
  placeholder:
    'Descreva pontos positivos e negativos que chamaram a atenção do cliente.',
  warningTitle: 'Enviaremos ao proprietário!',
  warningText:
    'Por isso, não informe nome, telefone e endereço; sejam eles seus, de clientes ou de outros proprietários.',
});

const form = reactive({
  imovel_id: null,
  contact_id: null,
  user_id: null,
  conversation_id: null,
  status: 'agendada',
  scheduled_at: '',
  confirm_after_creation: false,
  buyer_visit_intention: 'indefinido',
  observacoes: '',
  feedback: '',
  additional_attributes: {
    visitor_cpf: '',
    entry_authorization: 'A combinar',
    entry_access: {
      access_key: 'A_COMBINAR',
      doorman_type: '',
      house_occupancy: '',
      vacant_at: '',
      key_type: '',
      password: '',
      responsible_name: '',
      responsible_phone: '',
      responsible_address: '',
      instructions: '',
    },
    visit_reschedule: {
      reason: '',
    },
  },
});

const VISIT_FORM_STAGES = Object.freeze([
  { key: 'visitor', label: 'Cliente', icon: 'i-lucide-user-round' },
  { key: 'house', label: 'Imóvel', icon: 'i-lucide-home' },
  { key: 'datetime', label: 'Horário', icon: 'i-lucide-calendar-clock' },
  { key: 'details', label: 'Detalhes', icon: 'i-lucide-list-checks' },
]);

const agentes = ref([]);
const leadSearch = ref('');
const propertySearchQuery = ref('');
const selectedPropertySource = ref('portfolio');
const currentLead = ref(null);
const currentAgent = ref(null);
const currentImovel = ref(null);
const activeVisitFormStage = ref(VISIT_FORM_STAGES[0].key);
const visitContext = computed(() =>
  String(route.query.context || (isEditing ? 'reschedule' : 'schedule'))
    .toLowerCase()
    .trim()
);
const isRegisterContext = computed(() => visitContext.value === 'register');
const FORM_COPY_BY_MODE = Object.freeze({
  edit: {
    title: 'Horários para visita',
    helper:
      'Selecione abaixo os horários nos quais o seu imóvel pode receber visitas. Isso irá refletir no site da Imudei.',
    submitLabel: 'Confirmar horários',
    successMessage:
      'Visita agendada com sucesso! Em alguns instantes aparecerá na sua agenda.',
    errorMessage:
      'Falha ao confirmar visita! Ocorreu um erro ao processar essas informações. Tente novamente mais tarde.',
  },
  register: {
    title: 'Registrar visita realizada',
    helper:
      'Registre uma visita já realizada, com cliente, imóvel, horário e resultado operacional.',
    submitLabel: 'Registrar',
    successMessage:
      'Visita registrada com sucesso! Em alguns instantes aparecerá na sua agenda.',
    errorMessage: 'Erro ao registrar a visita',
  },
  schedule: {
    title: 'Nova visita',
    helper:
      'Agende uma visita para um cliente. Visitas sempre acompanhadas por alguém da imobiliária.',
    submitLabel: 'Agendar',
    successMessage:
      'Visita agendada com sucesso! Em alguns instantes aparecerá na sua agenda.',
    errorMessage:
      'Falha ao confirmar visita! Ocorreu um erro ao processar essas informações. Tente novamente mais tarde.',
  },
});
const formMode = computed(() => {
  if (isEditing) return 'edit';
  return isRegisterContext.value ? 'register' : 'schedule';
});
const formCopy = computed(() => FORM_COPY_BY_MODE[formMode.value]);
const formTitle = computed(() => formCopy.value.title);
const formHelper = computed(() => formCopy.value.helper);
const submitLabel = computed(() => formCopy.value.submitLabel);
const successMessage = computed(() => formCopy.value.successMessage);
const errorMessage = computed(() => formCopy.value.errorMessage);
const maxActiveVisitsPerContact = computed(() =>
  Number(
    currentAccount.value?.settings?.lais_visit_max_active_per_contact ||
      currentAccount.value?.lais_visit_max_active_per_contact ||
      0
  )
);
const normalizedPropertyBusinessTokens = computed(() =>
  [
    currentImovel.value?.finalidade,
    currentImovel.value?.business_context,
    currentImovel.value?.businessContext,
    currentImovel.value?.additional_attributes?.business_context,
    currentImovel.value?.additionalAttributes?.businessContext,
  ]
    .filter(Boolean)
    .map(value =>
      String(value)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    )
);
const isForSaleVisit = computed(() =>
  normalizedPropertyBusinessTokens.value.some(value =>
    ['venda', 'compra', 'comprar', 'sale', 'for_sale', 'for_selling'].includes(
      value
    )
  )
);
const shouldSubmitRegisterFeedback = computed(
  () => isRegisterContext.value && isForSaleVisit.value
);
const feedbackFieldLabel = computed(() =>
  shouldSubmitRegisterFeedback.value
    ? REGISTER_FEEDBACK_COPY.title
    : 'Resultado da visita'
);
const feedbackFieldHelper = computed(() =>
  shouldSubmitRegisterFeedback.value
    ? REGISTER_FEEDBACK_COPY.helper
    : 'Máximo de 650 caracteres para manter o histórico objetivo.'
);
const feedbackFieldPlaceholder = computed(() =>
  shouldSubmitRegisterFeedback.value ? REGISTER_FEEDBACK_COPY.placeholder : ''
);
const recentScheduledContacts = computed(() => {
  const seen = new Set();
  return (store.getVisitasList || [])
    .filter(visita => visita.contact?.id)
    .sort(
      (a, b) =>
        new Date(b.createdAt || b.scheduledAt || 0).getTime() -
        new Date(a.createdAt || a.scheduledAt || 0).getTime()
    )
    .map(visita => visita.contact)
    .filter(contact => {
      if (seen.has(contact.id)) return false;
      seen.add(contact.id);
      return true;
    })
    .slice(0, 3);
});
const selectedContactActiveVisitsCount = computed(() => {
  if (!form.contact_id) return 0;
  return (store.getVisitasList || []).filter(visita => {
    const status = visita.status || '';
    const scheduledAt = visita.scheduledAt || visita.scheduled_at;
    return (
      ['solicitada', 'agendada', 'confirmada', 'paralisada'].includes(status) &&
      Number(visita.contact?.id || visita.contactId || visita.contact_id) ===
        Number(form.contact_id) &&
      (!scheduledAt || new Date(scheduledAt).getTime() >= Date.now())
    );
  }).length;
});
const isStagedCreate = computed(() => !isEditing);
const visitStageCompletion = computed(() => ({
  visitor: Boolean(form.contact_id),
  house: Boolean(form.imovel_id),
  datetime: Boolean(form.scheduled_at),
  details: Boolean(
    form.status ||
      form.observacoes?.trim() ||
      form.feedback?.trim() ||
      form.confirm_after_creation
  ),
}));
const activeVisitFormStageIndex = computed(() =>
  VISIT_FORM_STAGES.findIndex(stage => stage.key === activeVisitFormStage.value)
);
const isLastVisitFormStage = computed(
  () => activeVisitFormStageIndex.value === VISIT_FORM_STAGES.length - 1
);
const isFirstVisitFormStage = computed(
  () => activeVisitFormStageIndex.value <= 0
);
const currentVisitFormStage = computed(
  () =>
    VISIT_FORM_STAGES[activeVisitFormStageIndex.value] || VISIT_FORM_STAGES[0]
);
const primaryActionLabel = computed(() =>
  isStagedCreate.value && !isLastVisitFormStage.value
    ? 'Continuar'
    : submitLabel.value
);
const primaryActionIcon = computed(() =>
  isStagedCreate.value && !isLastVisitFormStage.value
    ? 'i-lucide-arrow-right'
    : 'i-lucide-check'
);
const isVisitClosed = computed(() =>
  ['realizada', 'nao_compareceu'].includes(form.status)
);
const availabilityDays = computed(() => availability.value?.availability || []);
const activeAvailabilityDay = computed(
  () => availabilityDays.value[selectedAvailabilityDay.value] || null
);

const unavailableReasonLabels = {
  visiting_disabled_in_region: 'Fora da região atendida',
  scheduled_at_invalid: 'Horário inválido',
  scheduled_at_in_past: 'Horário já passou',
  slot_taken: 'Imóvel já tem visita',
  agent_slot_taken: 'Corretor já tem visita',
  agent_unavailable: 'Corretor indisponível',
  agent_in_transit: 'Corretor em deslocamento',
  visitor_slot_taken: 'Cliente já tem visita',
  contact_visit_limit_reached: 'Cliente no limite ativo',
  too_short_notice: 'Muito próximo para agendar',
  outside_window: 'Fora da janela permitida',
  invalid_weekday: 'Dia indisponível',
  invalid_interval: 'Horário fora da grade',
  property_unavailable: 'Imóvel indisponível',
};

const ensureOptionPresence = (listRef, record) => {
  if (!record?.id) return;
  if (listRef.value.some(item => Number(item.id) === Number(record.id))) return;
  listRef.value = [record, ...listRef.value];
};

const valueFrom = (item, ...keys) => {
  if (!item) return '';
  const attrs = item.additional_attributes || item.additionalAttributes || {};
  const match = keys.find(
    key =>
      (item[key] != null && item[key] !== '') ||
      (attrs[key] != null && attrs[key] !== '')
  );
  if (!match) return '';
  return item[match] != null && item[match] !== '' ? item[match] : attrs[match];
};

const entryAccessFieldValue = (source, ...keys) => {
  const match = keys.find(key => source?.[key] != null && source[key] !== '');
  return match ? source[match] : '';
};

const normalizedEntryAccessFrom = item => {
  const attrs = item?.additional_attributes || item?.additionalAttributes || {};
  const source =
    item?.entry_access ||
    item?.entryAccess ||
    attrs.entry_access ||
    attrs.entryAccess ||
    {};

  return {
    access_key: normalizeEntryAccessKey(
      entryAccessFieldValue(source, 'access_key', 'accessKey', 'kind') ||
        attrs.entry_authorization ||
        attrs.entryAuthorization
    ),
    doorman_type: entryAccessFieldValue(source, 'doorman_type', 'doormanType'),
    house_occupancy: entryAccessFieldValue(
      source,
      'house_occupancy',
      'houseOccupancy'
    ),
    vacant_at: entryAccessFieldValue(source, 'vacant_at', 'vacantAt'),
    key_type: entryAccessFieldValue(source, 'key_type', 'keyType'),
    password: entryAccessFieldValue(source, 'password'),
    responsible_name: entryAccessFieldValue(
      source,
      'responsible_name',
      'responsibleName'
    ),
    responsible_phone: entryAccessFieldValue(
      source,
      'responsible_phone',
      'responsiblePhone'
    ),
    responsible_address: entryAccessFieldValue(
      source,
      'responsible_address',
      'responsibleAddress'
    ),
    instructions: entryAccessFieldValue(source, 'instructions'),
  };
};

const hasEntryAccessDetails = access => {
  if (!access) return false;
  if (normalizeEntryAccessKey(access.access_key) !== 'A_COMBINAR') return true;

  return [
    access.doorman_type,
    access.house_occupancy,
    access.vacant_at,
    access.key_type,
    access.password,
    access.responsible_name,
    access.responsible_phone,
    access.responsible_address,
    access.instructions,
  ].some(Boolean);
};

const normalizedTipo = value => {
  const normalized = String(value || '').toLowerCase();
  return (
    {
      apartment: 'apartamento',
      apartamento: 'apartamento',
      house: 'casa',
      casa: 'casa',
      townhouse: 'casa_condominio',
      casa_condominio: 'casa_condominio',
      penthouse: 'cobertura',
      cobertura: 'cobertura',
      studio: 'studio',
      loft: 'loft',
      flat: 'flat',
      land: 'terreno',
      terreno: 'terreno',
      commercial: 'comercial',
      comercial: 'comercial',
      office: 'sala_comercial',
      sala_comercial: 'sala_comercial',
    }[normalized] || 'apartamento'
  );
};

const normalizedFinalidade = item => {
  const value = String(
    valueFrom(item, 'finalidade', 'transaction_type', 'transactionType') || ''
  ).toLowerCase();
  if (['rent', 'rental', 'aluguel'].includes(value)) return 'aluguel';
  if (['season', 'temporada'].includes(value)) return 'temporada';
  return 'venda';
};

const mediaFrom = item => {
  const media = valueFrom(item, 'midias', 'media', 'images', 'photos') || [];
  return Array.isArray(media)
    ? media.map(entry => (typeof entry === 'string' ? { url: entry } : entry))
    : [];
};

const titleFrom = item =>
  valueFrom(item, 'titulo', 'title', 'name', 'address') || 'Imóvel sem título';

const linkFrom = item =>
  valueFrom(
    item,
    'link_externo',
    'linkExterno',
    'external_url',
    'externalUrl',
    'public_url',
    'publicUrl',
    'contactWebsite',
    'url'
  );

const normalizedExternalImovelPayload = (item, source) => {
  const externalId = valueFrom(item, 'external_id', 'externalId', 'id');
  const catalogSource =
    valueFrom(item, 'portal_origem', 'portalOrigem') ||
    (source === 'properties' ? 'imobiliaria-api.Properties' : source);
  return {
    external_id: externalId ? String(externalId) : null,
    titulo: titleFrom(item),
    descricao: valueFrom(item, 'descricao', 'description'),
    tipo: normalizedTipo(valueFrom(item, 'tipo', 'type')),
    finalidade: normalizedFinalidade(item),
    status: valueFrom(item, 'status') || 'disponivel',
    valor_venda: valueFrom(
      item,
      'valor_venda',
      'valorVenda',
      'sale_price',
      'salePrice',
      'price'
    ),
    valor_aluguel: valueFrom(
      item,
      'valor_aluguel',
      'valorAluguel',
      'rent_price',
      'rentPrice'
    ),
    valor_iptu: valueFrom(item, 'valor_iptu', 'valorIptu', 'property_tax'),
    valor_condominio: valueFrom(
      item,
      'valor_condominio',
      'valorCondominio',
      'condo_price'
    ),
    quartos: valueFrom(item, 'quartos', 'bedrooms'),
    banheiros: valueFrom(item, 'banheiros', 'bathrooms'),
    vagas_garagem: valueFrom(item, 'vagas_garagem', 'vagasGaragem', 'parking'),
    area_util: valueFrom(item, 'area_util', 'areaUtil', 'usable_area'),
    area_total: valueFrom(item, 'area_total', 'areaTotal', 'total_area'),
    endereco: valueFrom(item, 'endereco', 'address', 'street'),
    bairro: valueFrom(item, 'bairro', 'neighborhood'),
    cidade: valueFrom(item, 'cidade', 'city'),
    uf: valueFrom(item, 'uf', 'state'),
    cep: valueFrom(item, 'cep', 'zip_code', 'zipCode'),
    latitude: valueFrom(item, 'latitude', 'lat'),
    longitude: valueFrom(item, 'longitude', 'lng'),
    portal_origem: catalogSource,
    link_externo: linkFrom(item),
    midias: mediaFrom(item),
    caracteristicas: valueFrom(item, 'caracteristicas', 'features') || [],
    additional_attributes: {
      ...(valueFrom(item, 'additional_attributes', 'additionalAttributes') ||
        {}),
      materialized_from_catalog: source,
      materialized_for: 'visit_flow',
      materialized_as_native: true,
      in_house_listing: true,
      in_house_listing_label: 'Imóveis na casa',
      source: catalogSource,
    },
  };
};

const materializedExternalIds = payload =>
  [
    payload.external_id ? String(payload.external_id) : '',
    payload.external_id && payload.portal_origem
      ? `${payload.portal_origem}:${payload.external_id}`
      : '',
  ].filter(Boolean);

const findMaterializedImovel = async payload => {
  if (!payload.external_id) return null;
  const { data } = await ImovelAPI.get({
    q: payload.external_id,
    page: 1,
    per_page: 10,
  });
  const externalIds = materializedExternalIds(payload);
  return (data.payload || []).find(item =>
    externalIds.includes(String(item.external_id || ''))
  );
};

const materializeExternalImovel = async (item, source) => {
  const payload = normalizedExternalImovelPayload(item, source);
  const existing = await findMaterializedImovel(payload);
  if (existing) return existing;

  const { data } = await ImovelAPI.create(payload);
  return data.payload || data;
};

const applyEntryAccessFromImovel = item => {
  const access = normalizedEntryAccessFrom(item);

  if (!hasEntryAccessDetails(access)) {
    editingAccess.value = true;
    return;
  }

  form.additional_attributes.entry_access = {
    ...form.additional_attributes.entry_access,
    ...access,
  };
  form.additional_attributes.entry_authorization = entryAccessLabel(
    access.access_key
  );
  editingAccess.value = false;
};

const canOpenVisitFormStage = key => {
  if (!isStagedCreate.value) return true;
  if (key === 'visitor') return true;
  if (key === 'house') return visitStageCompletion.value.visitor;
  if (key === 'datetime') {
    return (
      visitStageCompletion.value.visitor && visitStageCompletion.value.house
    );
  }
  if (key === 'details') {
    return (
      visitStageCompletion.value.visitor &&
      visitStageCompletion.value.house &&
      visitStageCompletion.value.datetime
    );
  }
  return false;
};

const isVisitStageVisible = key =>
  !isStagedCreate.value || activeVisitFormStage.value === key;

const visitStageButtonClass = key => {
  const isActive = activeVisitFormStage.value === key;
  const isComplete = visitStageCompletion.value[key];
  const isDisabled = !canOpenVisitFormStage(key);

  return [
    isActive
      ? 'border-lais-primary bg-lais-primary text-white shadow-sm'
      : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary',
    isComplete && !isActive
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : '',
    isDisabled
      ? 'cursor-not-allowed opacity-60 hover:border-lais-border hover:text-lais-text-body'
      : '',
  ];
};

const openVisitFormStage = key => {
  if (canOpenVisitFormStage(key)) {
    activeVisitFormStage.value = key;
    return;
  }

  if (key === 'house') {
    useAlert('Selecione um cliente para continuar.');
    return;
  }

  if (key === 'datetime') {
    useAlert('Selecione cliente e imóvel para continuar.');
    return;
  }

  useAlert('Selecione cliente, imóvel e horário para continuar.');
};

const moveToNextStageFrom = key => {
  if (!isStagedCreate.value || activeVisitFormStage.value !== key) return;
  const currentIndex = VISIT_FORM_STAGES.findIndex(stage => stage.key === key);
  const nextStage = VISIT_FORM_STAGES[currentIndex + 1];
  if (nextStage && canOpenVisitFormStage(nextStage.key)) {
    activeVisitFormStage.value = nextStage.key;
  }
};

const previousVisitFormStage = () => {
  if (!isStagedCreate.value || isFirstVisitFormStage.value) return;
  activeVisitFormStage.value =
    VISIT_FORM_STAGES[activeVisitFormStageIndex.value - 1].key;
};

const nextVisitFormStage = () => {
  if (!isStagedCreate.value || isLastVisitFormStage.value) return;

  if (
    currentVisitFormStage.value.key === 'visitor' &&
    !visitStageCompletion.value.visitor
  ) {
    useAlert('Selecione um cliente para continuar.');
    return;
  }
  if (
    currentVisitFormStage.value.key === 'house' &&
    !visitStageCompletion.value.house
  ) {
    useAlert('Selecione um imóvel para continuar.');
    return;
  }
  if (
    currentVisitFormStage.value.key === 'datetime' &&
    !visitStageCompletion.value.datetime
  ) {
    useAlert('Selecione ao menos 1 opção');
    return;
  }

  activeVisitFormStage.value =
    VISIT_FORM_STAGES[activeVisitFormStageIndex.value + 1].key;
};

const closingContextLabel = computed(() => {
  if (visitContext.value === 'register') return 'registro';
  if (visitContext.value === 'reschedule') return 'reagendamento';
  return 'agendamento';
});

const selectVisitProperty = async result => {
  selectedPropertySource.value = result.source;
  currentImovel.value = result.item;

  if (result.source === 'portfolio') {
    form.imovel_id = Number(valueFrom(result.item, 'id'));
    applyEntryAccessFromImovel(result.item);
    moveToNextStageFrom('house');
    return;
  }

  propertyMaterializing.value = true;
  try {
    const internalImovel = await materializeExternalImovel(
      result.item,
      result.source
    );
    currentImovel.value = internalImovel;
    selectedPropertySource.value = 'portfolio';
    form.imovel_id = Number(valueFrom(internalImovel, 'id'));
    applyEntryAccessFromImovel(internalImovel);
    moveToNextStageFrom('house');
    useAlert('Imóvel na casa selecionado para agendar a visita.');
  } catch {
    currentImovel.value = null;
    selectedPropertySource.value = 'portfolio';
    form.imovel_id = null;
    useAlert('Não foi possível preparar este imóvel para visita.');
  } finally {
    propertyMaterializing.value = false;
  }
};

const selectVisitContact = contact => {
  currentLead.value = contact;
  form.contact_id = contact.id;
  moveToNextStageFrom('visitor');
};

const formatDateTimeLocal = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = number => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const applyRegisterDefaults = () => {
  form.status = 'realizada';
  form.confirm_after_creation = true;
  if (!form.scheduled_at) {
    form.scheduled_at = formatDateTimeLocal(new Date().toISOString());
  }
};

const formatSlotTime = value =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

const availabilityDayKey = (day, index) =>
  day?.date_id || day?.dateId || day?.label || `day-${index}`;

const availabilityDayLabel = day => {
  const dateId = day?.date_id || day?.dateId || '';
  if (!dateId) return day?.label || 'Dia';

  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(`${dateId}T00:00:00Z`));
};

const unavailableReasonLabel = reason =>
  unavailableReasonLabels[reason] || 'Indisponível';

const isSelectedSlot = slot =>
  formatDateTimeLocal(slot.starts_at) === form.scheduled_at;

const isHighDemandSlot = slot =>
  slot?.is_high_demand || slot?.isHighDemand || slot?.high_demand;

const slotsForDay = day => (Array.isArray(day?.slots) ? day.slots : []);
const selectedSlotDay = computed(() =>
  availabilityDays.value.find(day =>
    slotsForDay(day).some(slot => isSelectedSlot(slot))
  )
);
const selectedSlot = computed(() => {
  if (!form.scheduled_at) return null;

  return (
    slotsForDay(selectedSlotDay.value).find(slot => isSelectedSlot(slot)) ||
    null
  );
});
const selectedSlotDate = computed(
  () => selectedSlotDay.value?.date_id || selectedSlotDay.value?.dateId || null
);
const selectedSlotBeginSlot = computed(
  () =>
    selectedSlot.value?.begin_slot ??
    selectedSlot.value?.beginSlot ??
    selectedSlot.value?.register_begin_slot ??
    selectedSlot.value?.registerBeginSlot ??
    null
);
const slotRequiresConfirmation = slot =>
  Boolean(
    slot?.requires_confirmation ||
      slot?.requiresConfirmation ||
      slot?.slot_behavior === 'required' ||
      slot?.slotBehavior === 'required'
  );

const selectedSlotWarning = computed(() => {
  if (!selectedSlot.value || selectedSlot.value.available) return '';

  return unavailableReasonLabel(selectedSlot.value.unavailable_reason);
});
const selectedHighDemandMessage = computed(() =>
  selectedSlot.value && slotRequiresConfirmation(selectedSlot.value)
    ? 'Essa visita está muito próxima, por isso precisa ser agendada e confirmada também.'
    : ''
);
const selectedSlotRequiresConfirmation = computed(
  () =>
    !isEditing &&
    !!selectedSlot.value &&
    slotRequiresConfirmation(selectedSlot.value) &&
    !form.confirm_after_creation
);
const entryAccess = computed(() => form.additional_attributes.entry_access);
const selectedEntryAccessHelper = computed(() =>
  entryAccessHelper(entryAccess.value.access_key)
);
const showEntryPasswordField = computed(
  () =>
    entryAccess.value.access_key === 'PASSWORD' ||
    entryAccess.value.key_type === 'PASSWORD'
);
const showResponsibleFields = computed(() =>
  [
    'KEYS_WITH_OWNER',
    'KEYS_WITH_TENANT_LIVING',
    'KEYS_WITH_EXTERNAL_RESPONSIBLE',
    'KEYS_WITH_PARTNER',
  ].includes(entryAccess.value.access_key)
);
const hasKeyHolderDetails = computed(
  () =>
    !showResponsibleFields.value ||
    Boolean(
      entryAccess.value.responsible_name?.trim() ||
        entryAccess.value.responsible_phone?.trim() ||
        entryAccess.value.responsible_address?.trim()
    )
);
const hasDefinedEntryAccess = computed(() =>
  hasEntryAccessDetails(entryAccess.value)
);
const showEntryAccessEditor = computed(
  () => editingAccess.value || !hasDefinedEntryAccess.value
);
const formatEntryAccessDate = value => {
  if (!value) return '';

  const date = new Date(
    String(value).includes('T') ? value : `${value}T00:00:00`
  );
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('pt-BR').format(date);
};
const entryAccessSummaryRows = computed(() =>
  [
    {
      icon: 'i-lucide-key-round',
      label: 'Entrada',
      value: entryAccessLabel(entryAccess.value.access_key),
    },
    entryAccess.value.doorman_type
      ? {
          icon: 'i-lucide-building-2',
          label: ENTRY_CONDITIONS_LABELS.frontdoor,
          value: doormanLabel(entryAccess.value.doorman_type),
        }
      : null,
    entryAccess.value.house_occupancy
      ? {
          icon: 'i-lucide-home',
          label: ENTRY_CONDITIONS_LABELS.occupancy,
          value: occupancyLabel(entryAccess.value.house_occupancy),
        }
      : null,
    entryAccess.value.vacant_at
      ? {
          icon: 'i-lucide-calendar-days',
          label: ENTRY_CONDITIONS_LABELS.vacantAt,
          value: formatEntryAccessDate(entryAccess.value.vacant_at),
        }
      : null,
    entryAccess.value.key_type
      ? {
          icon: 'i-lucide-fingerprint',
          label: ENTRY_CONDITIONS_LABELS.keyType,
          value: keyTypeLabel(entryAccess.value.key_type),
        }
      : null,
    entryAccess.value.responsible_name
      ? {
          icon: 'i-lucide-user-round',
          label: ENTRY_CONDITIONS_LABELS.responsibleName,
          value: entryAccess.value.responsible_name,
        }
      : null,
    entryAccess.value.responsible_phone
      ? {
          icon: 'i-lucide-phone',
          label: ENTRY_CONDITIONS_LABELS.responsiblePhone,
          value: entryAccess.value.responsible_phone,
        }
      : null,
    entryAccess.value.instructions
      ? {
          icon: 'i-lucide-list-checks',
          label: ENTRY_CONDITIONS_LABELS.instructions,
          value: entryAccess.value.instructions,
        }
      : null,
  ].filter(Boolean)
);
const isRescheduled = computed(
  () => isEditing && originalScheduledAt.value !== form.scheduled_at
);

const loadAvailability = async () => {
  availabilityError.value = '';
  selectedAvailabilityDay.value = 0;

  if (!form.imovel_id) {
    availability.value = null;
    return;
  }

  const requestId = availabilityRequestId.value + 1;
  availabilityRequestId.value = requestId;
  availabilityLoading.value = true;

  try {
    const payload = await store.availability({
      imovel_id: form.imovel_id,
      user_id: form.user_id,
      contact_id: form.contact_id,
      exclude_visita_id: isEditing ? route.params.id : null,
      days: 7,
    });

    if (availabilityRequestId.value === requestId) {
      availability.value = payload;
    }
  } catch (e) {
    if (availabilityRequestId.value === requestId) {
      availability.value = null;
      availabilityError.value = t('VISITS.ERRORS.LOAD_SLOTS');
    }
  } finally {
    if (availabilityRequestId.value === requestId) {
      availabilityLoading.value = false;
    }
  }
};

const selectSlot = slot => {
  if (!slot.available) return;

  form.scheduled_at = formatDateTimeLocal(slot.starts_at);
};

const buildPayload = () => ({
  imovel_id: form.imovel_id ? Number(form.imovel_id) : null,
  contact_id: form.contact_id ? Number(form.contact_id) : null,
  user_id: form.user_id ? Number(form.user_id) : null,
  conversation_id: form.conversation_id ? Number(form.conversation_id) : null,
  status: form.status,
  visit_type: isRegisterContext.value ? 'message' : 'presencial',
  date: selectedSlotDate.value,
  slot: selectedSlotBeginSlot.value,
  begin_slot: selectedSlotBeginSlot.value,
  scheduled_at: form.scheduled_at
    ? new Date(form.scheduled_at).toISOString()
    : null,
  buyer_visit_intention: form.buyer_visit_intention || 'indefinido',
  observacoes: form.observacoes?.trim() || '',
  feedback: form.feedback?.trim() || '',
  ...(isEditing ? {} : { confirm_after_creation: form.confirm_after_creation }),
  additional_attributes: {
    ...(form.additional_attributes || {}),
    visitor_cpf: form.additional_attributes?.visitor_cpf?.trim() || null,
    entry_authorization: entryAccessLabel(
      form.additional_attributes?.entry_access?.access_key
    ),
    entry_access: {
      ...(form.additional_attributes?.entry_access || {}),
      access_key: normalizeEntryAccessKey(
        form.additional_attributes?.entry_access?.access_key
      ),
      access_label: entryAccessLabel(
        form.additional_attributes?.entry_access?.access_key
      ),
      helper: entryAccessHelper(
        form.additional_attributes?.entry_access?.access_key
      ),
      doorman_label: doormanLabel(
        form.additional_attributes?.entry_access?.doorman_type
      ),
      house_occupancy_label: occupancyLabel(
        form.additional_attributes?.entry_access?.house_occupancy
      ),
      key_type_label: keyTypeLabel(
        form.additional_attributes?.entry_access?.key_type
      ),
    },
    visit_reschedule: isRescheduled.value
      ? {
          reason:
            form.additional_attributes?.visit_reschedule?.reason?.trim() ||
            'Ajuste de horário solicitado',
        }
      : form.additional_attributes?.visit_reschedule,
  },
});

const requestFeedbackStorageKey = visitId =>
  `${REQUEST_FEEDBACK_STORAGE_PREFIX}:${visitId}`;

const savePendingRequestFeedback = (visitId, feedback) => {
  if (!visitId || typeof window === 'undefined') return;

  window.sessionStorage?.setItem(
    requestFeedbackStorageKey(visitId),
    JSON.stringify({
      feedback,
      updatedAt: new Date().toISOString(),
    })
  );
};

const clearPendingRequestFeedback = visitId => {
  if (!visitId || typeof window === 'undefined') return;

  window.sessionStorage?.removeItem(requestFeedbackStorageKey(visitId));
};

const snapshotForm = () =>
  JSON.stringify({
    imovel_id: form.imovel_id,
    contact_id: form.contact_id,
    user_id: form.user_id,
    conversation_id: form.conversation_id,
    status: form.status,
    scheduled_at: form.scheduled_at,
    confirm_after_creation: form.confirm_after_creation,
    buyer_visit_intention: form.buyer_visit_intention,
    observacoes: form.observacoes,
    feedback: form.feedback,
    additional_attributes: form.additional_attributes,
  });

const isDirty = computed(
  () => !!initialSnapshot.value && snapshotForm() !== initialSnapshot.value
);

const assignFormFields = data => {
  [
    'imovel_id',
    'contact_id',
    'user_id',
    'conversation_id',
    'status',
    'buyer_visit_intention',
    'observacoes',
    'feedback',
  ].forEach(field => {
    if (Object.hasOwn(data, field)) {
      form[field] = data[field];
    }
  });

  form.additional_attributes = {
    ...form.additional_attributes,
    ...(data.additional_attributes || data.additionalAttributes || {}),
  };
  const attrs = data.additional_attributes || data.additionalAttributes || {};
  const entryAccessAttrs = attrs.entry_access || attrs.entryAccess || {};
  form.additional_attributes.entry_access = {
    ...form.additional_attributes.entry_access,
    ...entryAccessAttrs,
    access_key: normalizeEntryAccessKey(
      entryAccessAttrs.access_key ||
        entryAccessAttrs.accessKey ||
        attrs.entry_authorization ||
        attrs.entryAuthorization
    ),
  };
  form.additional_attributes.visit_reschedule = {
    ...form.additional_attributes.visit_reschedule,
    ...(attrs.visit_reschedule || attrs.visitReschedule || {}),
  };
};

const carregar = async () => {
  isLoading.value = true;
  try {
    try {
      const { data } = await AgentAPI.get();
      agentes.value = Array.isArray(data) ? data : data?.payload || [];
    } catch (e) {
      agentes.value = [];
    }

    if (isEditing) {
      const data = await store.show(route.params.id);
      if (data) {
        currentLead.value = data.contact || null;
        currentAgent.value = data.user || null;
        currentImovel.value = data.imovel || null;

        assignFormFields(data);
        form.scheduled_at = formatDateTimeLocal(data.scheduled_at);
        originalScheduledAt.value = form.scheduled_at;

        ensureOptionPresence(agentes, currentAgent.value);
      }
    } else if (route.query.imovel_id) {
      form.imovel_id = Number(route.query.imovel_id);
      try {
        const { data } = await ImovelAPI.show(route.query.imovel_id);
        currentImovel.value = data.payload || data;
        selectedPropertySource.value = 'portfolio';
        applyEntryAccessFromImovel(currentImovel.value);
      } catch {
        currentImovel.value = null;
      }
    }
  } catch (e) {
    useAlert(t('VISITS.ERRORS.LOAD_VISIT_DATA'));
  } finally {
    if (!isEditing && isRegisterContext.value) {
      applyRegisterDefaults();
    }
    initialSnapshot.value = snapshotForm();
    isLoading.value = false;
  }
};

const salvar = async () => {
  if (propertyMaterializing.value) {
    useAlert('Aguarde o imóvel ser preparado para agendar a visita.');
    return;
  }
  if (!form.imovel_id) {
    useAlert('Selecione um imóvel para agendar a visita.');
    return;
  }
  if (!form.contact_id) {
    useAlert('Selecione um cliente para agendar a visita.');
    return;
  }
  if (!hasKeyHolderDetails.value) {
    useAlert(KEY_HOLDER_4035_MESSAGE);
    return;
  }
  if (!form.scheduled_at) {
    useAlert('Selecione ao menos 1 opção');
    return;
  }
  if (shouldSubmitRegisterFeedback.value && !form.feedback?.trim()) {
    useAlert(REGISTER_FEEDBACK_REQUIRED_MESSAGE);
    return;
  }
  if (
    selectedSlotRequiresConfirmation.value &&
    !confirmationDialogAcknowledged.value
  ) {
    showVisitConfirmationDialog.value = true;
    return;
  }

  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (isEditing) {
      await store.update({ id: route.params.id, ...payload });
      useAlert('Horários para visitas atualizados com sucesso.');
      allowLeaving.value = true;
      initialSnapshot.value = snapshotForm();
      router.push(
        frontendURL(
          `accounts/${accountId.value}/visitas/${route.params.id}/sucesso?context=reschedule`
        )
      );
    } else {
      const created = await store.create(payload);
      const createdId = created?.id || created?.payload?.id;
      const feedbackText = form.feedback?.trim();
      let feedbackStatusQuery = '';

      if (shouldSubmitRegisterFeedback.value && feedbackText) {
        try {
          await store.submitRequestFeedback(createdId, {
            feedback: feedbackText,
          });
          clearPendingRequestFeedback(createdId);
          feedbackStatusQuery = '&feedback_status=sent';
        } catch {
          savePendingRequestFeedback(createdId, feedbackText);
          feedbackStatusQuery = '&feedback_status=error';
        }
      }

      useAlert(successMessage.value);
      allowLeaving.value = true;
      initialSnapshot.value = snapshotForm();
      router.push(
        frontendURL(
          `accounts/${accountId.value}/visitas/${createdId}/sucesso?context=${visitContext.value}${feedbackStatusQuery}`
        )
      );
    }
  } catch (error) {
    const messages = error?.response?.data?.errors;
    useAlert(
      Array.isArray(messages) && messages.length
        ? messages.join(', ')
        : errorMessage.value
    );
  } finally {
    isSaving.value = false;
  }
};

const runPrimaryAction = () => {
  if (isStagedCreate.value && !isLastVisitFormStage.value) {
    nextVisitFormStage();
    return;
  }

  salvar();
};

const confirmHighDemandVisit = () => {
  form.confirm_after_creation = true;
  confirmationDialogAcknowledged.value = true;
  showVisitConfirmationDialog.value = false;
  salvar();
};

const targetVisitsPath = () =>
  frontendURL(`accounts/${accountId.value}/visitas`);

const requestLeave = () => {
  if (isDirty.value) {
    pendingLeaveRoute.value = null;
    showClosingDialog.value = true;
    return;
  }

  allowLeaving.value = true;
  router.push(targetVisitsPath());
};

const keepEditing = () => {
  pendingLeaveRoute.value = null;
  showClosingDialog.value = false;
};

const confirmDiscard = () => {
  allowLeaving.value = true;
  showClosingDialog.value = false;

  if (pendingLeaveRoute.value) {
    router.push(pendingLeaveRoute.value.fullPath || pendingLeaveRoute.value);
    return;
  }

  router.push(targetVisitsPath());
};

const cancelar = () => requestLeave();

const revisarVisita = () =>
  router.push(
    frontendURL(
      `accounts/${accountId.value}/visitas/${route.params.id}/revisar`
    )
  );

onMounted(carregar);

onBeforeRouteLeave((to, from, next) => {
  if (allowLeaving.value || !isDirty.value) {
    next();
    return;
  }

  pendingLeaveRoute.value = to;
  showClosingDialog.value = true;
  next(false);
});

watch(
  () => [form.imovel_id, form.user_id, form.contact_id],
  () => {
    loadAvailability();
  }
);

watch(availabilityDays, days => {
  if (!days.length) {
    selectedAvailabilityDay.value = 0;
    return;
  }
  if (selectedAvailabilityDay.value >= days.length) {
    selectedAvailabilityDay.value = 0;
  }
});

const input =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-lais-bg-page font-redHatText md:overflow-hidden"
  >
    <LaisHeader
      :title="formTitle"
      :helper="formHelper"
      sticky
      :breadcrumb="[
        { label: 'Visitas', to: { name: 'visitas_index' } },
        {
          label: isEditing
            ? 'Editar'
            : isRegisterContext
              ? 'Registrar'
              : 'Nova',
        },
      ]"
    />

    <div
      class="min-h-0 flex-1 overflow-auto pb-[calc(5rem+env(safe-area-inset-bottom))] sm:pb-0 max-md:flex-none max-md:overflow-visible"
    >
      <div
        v-if="isLoading"
        class="py-16 text-center text-[13px] text-lais-text-muted"
      >
        {{ t('VISITS.COMMON.LOADING') }}
      </div>
      <form
        v-else
        class="flex w-full flex-col gap-5 px-4 py-8 sm:px-6 lg:px-10"
        @submit.prevent="runPrimaryAction"
      >
        <div
          v-if="isStagedCreate"
          class="flex flex-wrap items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-3"
        >
          <button
            v-for="(stage, index) in VISIT_FORM_STAGES"
            :key="stage.key"
            type="button"
            class="inline-flex h-10 min-w-[116px] items-center justify-center gap-2 rounded-full border px-3 text-[13px] font-semibold transition-colors"
            :class="visitStageButtonClass(stage.key)"
            :aria-current="
              activeVisitFormStage === stage.key ? 'step' : undefined
            "
            :aria-disabled="!canOpenVisitFormStage(stage.key)"
            @click="openVisitFormStage(stage.key)"
          >
            <span
              class="flex size-5 items-center justify-center rounded-full text-[11px]"
              :class="
                activeVisitFormStage === stage.key
                  ? 'bg-white/20 text-white'
                  : visitStageCompletion[stage.key]
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-lais-bg-soft text-lais-text-muted'
              "
            >
              <span
                v-if="visitStageCompletion[stage.key]"
                class="i-lucide-check size-3.5"
                aria-hidden="true"
              />
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span :class="stage.icon" class="size-4" aria-hidden="true" />
            {{ stage.label }}
          </button>
        </div>

        <LaisCard v-show="isVisitStageVisible('visitor')" title="Cliente">
          <div class="grid gap-4">
            <LaisContactSearch
              v-model="currentLead"
              v-model:query="leadSearch"
              required
              label="Buscar cliente"
              helper="Busque por nome, telefone, e-mail ou identificador e selecione o cliente em cards."
              :recent-contacts="recentScheduledContacts"
              :active-count="selectedContactActiveVisitsCount"
              :active-limit="maxActiveVisitsPerContact"
              @select="selectVisitContact"
            />
            <div
              class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
            >
              Para encontrar clientes fora da sua carteira, insira o e-mail ou
              telefone. A busca pelo nome encontra apenas clientes da sua
              carteira.
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LaisField
                label="CPF do visitante"
                helper="Opcional, usado para portaria e validação da visita."
              >
                <input
                  v-model="form.additional_attributes.visitor_cpf"
                  type="text"
                  inputmode="numeric"
                  autocomplete="off"
                  placeholder="000.000.000-00"
                  :class="input"
                />
              </LaisField>
              <LaisField
                label="ID da conversa"
                helper="Opcional, para vincular o agendamento ao atendimento"
              >
                <input
                  v-model="form.conversation_id"
                  type="number"
                  min="1"
                  :class="input"
                  placeholder="Ex: 1024"
                />
              </LaisField>
            </div>
            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <LaisField label="Tipo de visita">
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="option in VISIT_INTENTION_OPTIONS"
                    :key="option.value"
                    type="button"
                    class="rounded-full border px-3 py-2 text-[13px] font-semibold transition-colors"
                    :class="
                      form.buyer_visit_intention === option.value
                        ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                        : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
                    "
                    :aria-pressed="form.buyer_visit_intention === option.value"
                    @click="form.buyer_visit_intention = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </LaisField>
            </div>
          </div>
        </LaisCard>

        <LaisCard
          v-show="isVisitStageVisible('house')"
          title="Imóvel & entrada"
        >
          <div class="grid gap-4">
            <LaisPropertySearch
              v-model="currentImovel"
              v-model:source="selectedPropertySource"
              v-model:query="propertySearchQuery"
              required
              include-empreendimentos
              treat-properties-as-portfolio
              label="Buscar imóvel"
              helper="Busca integrada na carteira, imóveis na casa e empreendimentos por código, rua, bairro, cidade ou endereço completo."
              @select="selectVisitProperty"
            />
            <div
              v-if="propertyMaterializing"
              class="flex items-center gap-2 rounded-lais border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body"
            >
              <span
                class="i-lucide-loader-circle size-4 animate-spin text-lais-primary"
                aria-hidden="true"
              />
              Preparando imóvel na casa para liberar horários.
            </div>
            <div
              class="grid gap-4 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-4"
            >
              <div
                class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div class="min-w-0">
                  <h3 class="text-[14px] font-semibold text-lais-text">
                    Entrada no imóvel
                  </h3>
                  <p
                    class="mt-1 text-[13px] leading-relaxed text-lais-text-muted"
                  >
                    {{
                      showEntryAccessEditor
                        ? 'Revise como o corretor irá acessar o imóvel.'
                        : 'Acesso já definido para este imóvel.'
                    }}
                  </p>
                </div>
                <LaisPrimaryButton
                  v-if="hasDefinedEntryAccess"
                  variant="ghost"
                  :icon="
                    showEntryAccessEditor ? 'i-lucide-list' : 'i-lucide-pencil'
                  "
                  @click="editingAccess = !editingAccess"
                >
                  {{ showEntryAccessEditor ? 'Ver resumo' : 'Editar acesso' }}
                </LaisPrimaryButton>
              </div>

              <div v-if="!showEntryAccessEditor" class="grid gap-1">
                <LaisInfoRow
                  v-for="row in entryAccessSummaryRows"
                  :key="row.label"
                  :icon="row.icon"
                  :label="row.label"
                  :value="row.value"
                />
              </div>

              <div v-else class="grid gap-4">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <LaisField
                    label="Entrada no imóvel"
                    helper="Como o corretor irá acessar o imóvel."
                  >
                    <select
                      v-model="
                        form.additional_attributes.entry_access.access_key
                      "
                      :class="input"
                    >
                      <option
                        v-for="option in ENTRY_AUTHORIZATION_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </LaisField>
                </div>
                <div
                  class="rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
                >
                  {{ selectedEntryAccessHelper }}
                </div>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <LaisField :label="ENTRY_CONDITIONS_LABELS.frontdoor">
                    <select
                      v-model="
                        form.additional_attributes.entry_access.doorman_type
                      "
                      :class="input"
                    >
                      <option value="">Selecione o valor</option>
                      <option
                        v-for="option in ENTRY_DOORMAN_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </LaisField>
                  <LaisField :label="ENTRY_CONDITIONS_LABELS.occupancy">
                    <select
                      v-model="
                        form.additional_attributes.entry_access.house_occupancy
                      "
                      :class="input"
                    >
                      <option value="">Selecione o valor</option>
                      <option
                        v-for="option in ENTRY_OCCUPANCY_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </LaisField>
                  <LaisField :label="ENTRY_CONDITIONS_LABELS.keyType">
                    <select
                      v-model="form.additional_attributes.entry_access.key_type"
                      :class="input"
                    >
                      <option value="">Selecione o valor</option>
                      <option
                        v-for="option in ENTRY_KEY_TYPE_OPTIONS"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </LaisField>
                </div>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <LaisField
                    v-if="
                      form.additional_attributes.entry_access
                        .house_occupancy === 'VACANT'
                    "
                    :label="ENTRY_CONDITIONS_LABELS.vacantAt"
                  >
                    <input
                      v-model="
                        form.additional_attributes.entry_access.vacant_at
                      "
                      type="date"
                      :class="input"
                    />
                  </LaisField>
                  <LaisField
                    v-if="showEntryPasswordField"
                    :label="ENTRY_CONDITIONS_LABELS.password"
                  >
                    <input
                      v-model="form.additional_attributes.entry_access.password"
                      type="text"
                      :class="input"
                      placeholder="Informe a senha"
                    />
                  </LaisField>
                </div>
                <div v-if="showResponsibleFields" class="grid gap-4">
                  <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <LaisField :label="ENTRY_CONDITIONS_LABELS.responsibleName">
                      <input
                        v-model="
                          form.additional_attributes.entry_access
                            .responsible_name
                        "
                        type="text"
                        :class="input"
                        placeholder="Nome do responsável"
                      />
                    </LaisField>
                    <LaisField
                      :label="ENTRY_CONDITIONS_LABELS.responsiblePhone"
                      :helper="ENTRY_CONDITIONS_LABELS.responsiblePhoneHelper"
                    >
                      <input
                        v-model="
                          form.additional_attributes.entry_access
                            .responsible_phone
                        "
                        type="tel"
                        :class="input"
                        placeholder="Telefone de contato"
                      />
                    </LaisField>
                  </div>
                  <LaisField
                    :label="ENTRY_CONDITIONS_LABELS.responsibleAddress"
                    :helper="ENTRY_CONDITIONS_LABELS.responsibleAddressHelper"
                  >
                    <input
                      v-model="
                        form.additional_attributes.entry_access
                          .responsible_address
                      "
                      type="text"
                      :class="input"
                      placeholder="Endereço do responsável"
                    />
                  </LaisField>
                </div>
                <LaisField :label="ENTRY_CONDITIONS_LABELS.instructions">
                  <textarea
                    v-model="
                      form.additional_attributes.entry_access.instructions
                    "
                    rows="3"
                    :class="input"
                    placeholder="Ex: retirar autorização na portaria, avisar responsável ao chegar."
                  />
                </LaisField>
              </div>
            </div>
          </div>
        </LaisCard>

        <LaisCard
          v-show="isVisitStageVisible('datetime')"
          title="Horários para visita"
        >
          <div class="grid gap-4">
            <LaisField label="Corretor" helper="Opcional">
              <select v-model.number="form.user_id" :class="input">
                <option :value="null">Qualquer corretor</option>
                <option v-for="a in agentes" :key="a.id" :value="a.id">
                  {{ a.name }}
                </option>
              </select>
            </LaisField>
            <div
              class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
            >
              <p>
                Selecione abaixo os horários nos quais o seu imóvel pode receber
                visitas. Isso irá refletir no site da Imudei.
              </p>
              <p class="mt-2 font-semibold text-lais-primary">
                Quanto maior a disponibilidade de horários de visita, maiores as
                chances do interessado certo encontrar o seu imóvel.
              </p>
              <p class="mt-2 text-lais-text-helper">
                Sem tempo para visitas? Inative ou despublique o imóvel.
              </p>
            </div>
            <div
              v-if="!form.imovel_id"
              class="rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-4 py-5 text-center text-[13px] text-lais-text-muted"
            >
              Selecione um imóvel para ver os horários.
            </div>
            <div
              v-else-if="availabilityLoading"
              class="rounded-lais-sm border border-lais-border bg-white px-4 py-5 text-center text-[13px] text-lais-text-muted"
            >
              Carregando horários disponíveis...
            </div>
            <div
              v-else-if="availabilityError"
              class="rounded-lais-sm border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700"
            >
              Houve algum problema ao carregar os dados, favor tente mais tarde.
            </div>
            <div
              v-else-if="availabilityDays.length"
              class="grid gap-4 rounded-lais-sm border border-lais-border bg-white p-3"
            >
              <div
                class="flex gap-2 overflow-x-auto pb-1 no-scrollbar"
                role="tablist"
                aria-label="Dias disponíveis para visita"
              >
                <button
                  v-for="(day, index) in availabilityDays"
                  :key="availabilityDayKey(day, index)"
                  type="button"
                  role="tab"
                  :aria-selected="selectedAvailabilityDay === index"
                  class="min-w-[88px] rounded-lais-sm border px-3 py-2 text-center text-[13px] font-semibold transition-colors"
                  :class="
                    selectedAvailabilityDay === index
                      ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                      : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary'
                  "
                  @click="selectedAvailabilityDay = index"
                >
                  {{ availabilityDayLabel(day) }}
                </button>
              </div>

              <div v-if="activeAvailabilityDay" class="grid gap-3">
                <div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <button
                    v-for="slot in activeAvailabilityDay.slots"
                    :key="slot.starts_at"
                    type="button"
                    :disabled="!slot.available"
                    :aria-pressed="isSelectedSlot(slot)"
                    :title="
                      slot.available
                        ? 'Disponível'
                        : unavailableReasonLabel(slot.unavailable_reason)
                    "
                    class="min-h-[62px] rounded-lais-sm border px-3 py-2 text-left transition-all disabled:cursor-not-allowed"
                    :class="[
                      isSelectedSlot(slot)
                        ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                        : slot.available
                          ? 'border-lais-border bg-white text-lais-text hover:border-lais-primary'
                          : 'border-lais-border bg-lais-bg-soft text-lais-text-muted opacity-80',
                    ]"
                    @click="selectSlot(slot)"
                  >
                    <span class="block text-[13px] font-semibold">
                      {{ formatSlotTime(slot.starts_at) }}
                    </span>
                    <span
                      v-if="isHighDemandSlot(slot)"
                      class="mt-1 inline-flex w-fit rounded-full border border-lais-primary/20 bg-lais-primary-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-lais-primary"
                    >
                      {{ VISIT_HIGH_DEMAND_LABEL }}
                    </span>
                    <span class="mt-1 block text-[11px] leading-tight">
                      {{
                        slot.available
                          ? 'Disponível'
                          : unavailableReasonLabel(slot.unavailable_reason)
                      }}
                    </span>
                  </button>
                </div>
                <p
                  v-if="!activeAvailabilityDay.slots?.length"
                  class="rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-4 py-5 text-center text-[13px] text-lais-text-muted"
                >
                  Não há horários disponíveis para este dia.
                </p>
              </div>
            </div>
            <div
              v-else
              class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-5 text-center text-[13px] text-lais-text-muted"
            >
              {{ unavailableReasonLabel(availability?.reason) }}
            </div>
            <div
              v-if="selectedSlotRequiresConfirmation"
              class="rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-4 py-3 text-[13px] font-semibold leading-relaxed text-lais-text"
            >
              {{ selectedHighDemandMessage }}
            </div>

            <LaisField
              label="Data e hora"
              required
              :helper="
                selectedSlotWarning ||
                'Selecione ao menos 1 opção ou ajuste manualmente quando precisar registrar um horário externo.'
              "
            >
              <input
                v-model="form.scheduled_at"
                type="datetime-local"
                required
                :class="input"
              />
            </LaisField>
            <LaisField
              v-if="isRescheduled"
              label="Motivo do reagendamento"
              helper="Esse motivo ficará registrado no histórico da visita."
            >
              <textarea
                v-model="form.additional_attributes.visit_reschedule.reason"
                rows="2"
                :class="input"
                placeholder="Ex: cliente pediu antecipação"
              />
            </LaisField>
          </div>
        </LaisCard>

        <LaisCard v-show="isVisitStageVisible('details')" title="Detalhes">
          <div class="grid gap-4">
            <LaisField label="Status">
              <select v-model="form.status" :class="input">
                <option value="agendada">Visita agendada</option>
                <option value="confirmada">Visita confirmada</option>
                <option value="realizada">Visita realizada</option>
                <option value="cancelada">Visita cancelada</option>
                <option value="nao_compareceu">Visita não realizada</option>
              </select>
            </LaisField>
            <div
              v-if="!isEditing"
              class="grid gap-2 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body"
            >
              <div>
                <strong class="block text-lais-text">
                  Pré-confirmação da visita
                </strong>
                <span class="mt-1 block text-lais-text-muted">
                  Escolha se esta visita já deve nascer confirmada ou se ainda
                  precisa aguardar validação adicional.
                </span>
                <span
                  v-if="!form.confirm_after_creation"
                  class="mt-2 block text-lais-text-helper"
                >
                  <strong>Cancelamento automático:</strong> visitas aguardando
                  confirmação seguem a regra de cancelamento caso o responsável
                  não confirme dentro do prazo.
                </span>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-full border px-3 py-2 text-[12px] font-semibold transition-colors"
                  :class="
                    form.confirm_after_creation
                      ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                      : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
                  "
                  :aria-pressed="form.confirm_after_creation"
                  @click="form.confirm_after_creation = true"
                >
                  Confirmar visita
                </button>
                <button
                  type="button"
                  class="rounded-full border px-3 py-2 text-[12px] font-semibold transition-colors"
                  :class="
                    !form.confirm_after_creation
                      ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                      : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
                  "
                  :aria-pressed="!form.confirm_after_creation"
                  @click="form.confirm_after_creation = false"
                >
                  Aguardar confirmação
                </button>
              </div>
            </div>
            <LaisField
              label="Observações"
              helper="Detalhes que ajudem o corretor a se preparar."
            >
              <textarea v-model="form.observacoes" rows="3" :class="input" />
            </LaisField>
            <LaisField
              v-if="isRegisterContext || isVisitClosed"
              :label="feedbackFieldLabel"
              :helper="feedbackFieldHelper"
            >
              <p
                v-if="shouldSubmitRegisterFeedback"
                class="mb-2 text-[13px] font-semibold text-lais-text"
              >
                {{ REGISTER_FEEDBACK_COPY.question }}
              </p>
              <textarea
                v-model="form.feedback"
                rows="3"
                maxlength="650"
                :placeholder="feedbackFieldPlaceholder"
                :class="input"
              />
              <p class="mt-1 text-right text-[12px] text-lais-text-muted">
                {{ form.feedback.length }}/650
              </p>
              <div
                v-if="shouldSubmitRegisterFeedback"
                class="mt-3 rounded-lais-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] leading-5 text-amber-900"
              >
                <p class="font-semibold">
                  {{ REGISTER_FEEDBACK_COPY.warningTitle }}
                </p>
                <p class="mt-1 text-amber-800">
                  {{ REGISTER_FEEDBACK_COPY.warningText }}
                </p>
              </div>
            </LaisField>
          </div>
        </LaisCard>
      </form>
    </div>

    <LaisActionBar sticky-mobile>
      <template #actions>
        <LaisPrimaryButton variant="ghost" @click="cancelar">
          Cancelar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="isStagedCreate && !isFirstVisitFormStage"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="previousVisitFormStage"
        >
          Voltar
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="isEditing"
          variant="ghost"
          icon="i-lucide-clipboard-check"
          @click="revisarVisita"
        >
          Revisar
        </LaisPrimaryButton>
      </template>
      <template #trailing>
        <LaisPrimaryButton
          variant="primary"
          :icon="primaryActionIcon"
          :is-loading="isSaving"
          @click="runPrimaryAction"
        >
          {{ primaryActionLabel }}
        </LaisPrimaryButton>
      </template>
    </LaisActionBar>

    <LaisModal
      :show="showVisitConfirmationDialog"
      title="Você deseja confirmar a visita?"
      max-width-class="max-w-[520px]"
      @close="showVisitConfirmationDialog = false"
    >
      <p class="text-[14px] leading-relaxed text-lais-text-body">
        <strong class="font-semibold text-lais-text">
          Essa visita está muito próxima, por isso precisa ser agendada e
          confirmada também.
        </strong>
        Ao agendar, você está garantindo que conseguirá realizar essa visita.
      </p>

      <template #footer>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton
            variant="ghost"
            @click="showVisitConfirmationDialog = false"
          >
            Alterar agendamento
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-calendar-check"
            :is-loading="isSaving"
            @click="confirmHighDemandVisit"
          >
            Agendar e confirmar visita
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <LaisModal
      :show="showClosingDialog"
      title="Deseja mesmo sair?"
      max-width-class="max-w-[520px]"
      @close="keepEditing"
    >
      <p class="text-[14px] leading-relaxed text-lais-text-body">
        Ao sair, todas as informações inseridas no {{ closingContextLabel }}
        serão perdidas.
      </p>

      <template #footer>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton variant="ghost" @click="keepEditing">
            Cancelar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            icon="i-lucide-trash-2"
            variant="primary"
            @click="confirmDiscard"
          >
            Sair
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>
  </LaisGamificationSurface>
</template>
